import { Component, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { STATES_LIST } from '../../../rates/config/filter-options';
import { CaseBoxState } from '../../../../shared/case-box/case-box.types';

/**
 * Collapsible filter form for the broker rate-sheet rails. Holds the common
 * knobs (State · Age · Premium · Term · Rating · Tax · MVA) bound to the
 * sheet's state service (CaseBoxState); per-type extras are projected via
 * <ng-content> (e.g. the IA/RILA Index/Strategy/Downside controls).
 */
@Component({
  selector: 'app-rate-sheet-filters',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="rail-section" [class.is-open]="open()">
      <button type="button" class="section-hdr" (click)="open.set(!open())">
        <svg class="sec-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M9 6l6 6-6 6"/>
        </svg>
        <span>Filters</span>
      </button>

      <div class="section-body">
        <div class="cgrid">
          <div class="cf">
            <label>State</label>
            <select class="ctl" [ngModel]="state.stateFilter()" (ngModelChange)="state.stateFilter.set($event)">
              <option [ngValue]="''">All states</option>
              @for (s of statesList; track s) { <option [ngValue]="s">{{ s }}</option> }
            </select>
          </div>

          @if (showAge) {
            <div class="cf">
              <label>Age</label>
              <input class="ctl" type="number" min="0" max="120" placeholder="—"
                     [ngModel]="state.clientAge()" (ngModelChange)="state.clientAge.set($event)">
            </div>
          }

          @if (showPremium) {
            <div class="cf full">
              <label>Premium</label>
              <input class="slider" type="range" min="10000" max="500000" step="5000"
                     [value]="state.premium()" (input)="state.premium.set(+$any($event.target).value)">
              <span class="cf-val">{{ fmtCurrency(state.premium()) }}</span>
            </div>
          }

          @if (showTerm) {
            <div class="cf full">
              <label>{{ termLabel }}</label>
              <span class="range">
                <select class="rng" [ngModel]="state.termMin()" (ngModelChange)="state.termMin.set($event)">
                  <option [ngValue]="null">Min</option>
                  @for (t of state.availableTerms(); track t) { <option [ngValue]="t">{{ t }}</option> }
                </select>
                <span class="dash">–</span>
                <select class="rng" [ngModel]="state.termMax()" (ngModelChange)="state.termMax.set($event)">
                  <option [ngValue]="null">Max</option>
                  @for (t of state.availableTerms(); track t) { <option [ngValue]="t">{{ t }}</option> }
                </select>
              </span>
            </div>
          }

          @if (showRating) {
            <div class="cf">
              <label>Min rating</label>
              <select class="ctl" [ngModel]="state.minRating()" (ngModelChange)="state.minRating.set($event)">
                <option [ngValue]="0">Any</option>
                <option [ngValue]="1">A- or better</option>
                <option [ngValue]="2">A or better</option>
                <option [ngValue]="3">A+ or better</option>
              </select>
            </div>
          }

          @if (showTax) {
            <div class="cf">
              <label>Tax status</label>
              <select class="ctl" [ngModel]="state.taxFilter()" (ngModelChange)="state.taxFilter.set($event)">
                <option [ngValue]="''">Q &amp; NQ</option>
                <option [ngValue]="'q'">Qualified</option>
                <option [ngValue]="'nq'">Non-qual.</option>
              </select>
            </div>
          }

          @if (showMva) {
            <div class="cf">
              <label>MVA</label>
              <select class="ctl" [ngModel]="state.mvaFilter()" (ngModelChange)="state.mvaFilter.set($event)">
                <option [ngValue]="''">Any</option>
                <option [ngValue]="'no'">No MVA</option>
                <option [ngValue]="'yes'">MVA only</option>
              </select>
            </div>
          }
        </div>

        <!-- Per-type extras (IA/RILA Refine controls) -->
        <ng-content></ng-content>

        <div class="foot">
          <span class="cnt">{{ state.resultCount() }} {{ unit }}</span>
          <span class="act" (click)="reset()">↺ Reset</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .rail-section { border-bottom: 1px solid var(--border-light); }
    .section-hdr {
      display: flex; align-items: center; gap: 8px; width: 100%;
      padding: 10px 12px 9px;
      font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
      color: var(--text2); text-transform: uppercase; text-align: left;
      background: none; border: none; cursor: pointer; font-family: inherit;
      transition: background 0.12s, color 0.12s;
    }
    .section-hdr:hover { background: var(--blue-soft); color: var(--navy); }
    .sec-caret { color: var(--muted); transition: transform 0.18s; width: 12px; height: 12px; flex-shrink: 0; }
    .rail-section.is-open .sec-caret { transform: rotate(90deg); }
    .rail-section.is-open { border-left: 3px solid var(--navy); }
    .rail-section.is-open .section-hdr { padding-left: 9px; background: var(--blue-soft); color: var(--navy); }
    .section-body { display: none; padding: 2px 14px 12px; }
    .rail-section.is-open .section-body { display: block; padding-left: 11px; }

    .ctl { font-family: var(--sans); font-size: 12px; border: 1px solid var(--border); border-radius: 6px; padding: 4px 6px; background: #fff; color: var(--text); }
    .cgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .cgrid .full { grid-column: 1 / -1; }
    .cf { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    .cf label { font-size: 10.5px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; }
    .cf .ctl { width: 100%; min-width: 0; }
    .cf .slider { width: 100%; accent-color: var(--blue); margin: 3px 0 0; }
    .cf-val { font-family: var(--mono); font-weight: 600; color: var(--navy); font-size: 13px; margin-top: 1px; }
    .range { display: flex; align-items: center; gap: 5px; }
    .range .rng { flex: 1; min-width: 0; font-size: 12px; border: 1px solid var(--border); border-radius: 6px; padding: 4px 6px; background: #fff; color: var(--text); }
    .range .dash { color: var(--muted); font-weight: 600; }
    .foot { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; border-top: 1px dashed var(--border-light); padding-top: 8px; }
    .foot .cnt { font-size: 11px; color: var(--text2); font-weight: 600; }
    .foot .act { font-size: 11px; color: var(--blue); font-weight: 600; cursor: pointer; }
  `],
})
export class RateSheetFiltersComponent {
  @Input({ required: true }) state!: CaseBoxState;
  @Input() showAge = true;
  @Input() showPremium = true;
  @Input() showTerm = true;
  @Input() showRating = true;
  @Input() showTax = false;
  @Input() showMva = true;
  @Input() termLabel = 'Term (yr)';
  @Input() unit = 'products';

  protected readonly open = signal(true);

  readonly statesList = STATES_LIST;

  reset(): void {
    this.state.stateFilter.set('');
    this.state.clientAge.set(null);
    this.state.premium.set(100_000);
    this.state.termMin.set(null);
    this.state.termMax.set(null);
    this.state.minRating.set(0);
    this.state.taxFilter.set('');
    this.state.mvaFilter.set('');
  }

  fmtCurrency(n: number): string {
    return '$' + Math.round(n).toLocaleString();
  }
}
